import React from 'react';
import { Helmet } from 'react-helmet-async';
import { designTokens } from '../utils/designTokens';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>Terms of Service - Boop</title>
        <meta name="description" content="Terms of Service for Boop - Community-driven AI prompt sharing platform." />
      </Helmet>

      <div className={`max-w-4xl mx-auto px-${designTokens.spacing.pagePadding} py-12`}>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Terms of Service</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">Last Updated: October 31, 2025</p>

        <div className="prose prose-lg dark:prose-invert max-w-none">

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 dark:text-gray-300">
              By accessing or using Boop ("Platform", "Service"), you agree to be bound by these Terms of Service ("Terms").
              If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Account Registration</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>You must be at least 13 years old to use this Service</li>
              <li>You must provide accurate, complete, and current registration information</li>
              <li>You are responsible for safeguarding your account credentials</li>
              <li>Each user may maintain only one account</li>
              <li>You are responsible for all activities that occur under your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. User Content</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Ownership:</strong> You retain all ownership rights to content you create and publish on Boop.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>License Grant:</strong> By posting content, you grant Boop a worldwide, non-exclusive, royalty-free
              license to use, display, reproduce, and distribute your content on the Platform.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <strong>Representations:</strong> You represent and warrant that you have all necessary rights to share
              the content you post and that it does not violate any third-party rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Content Guidelines</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">The following types of content are prohibited:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Harassment, hate speech, threats, or bullying</li>
              <li>Spam, misleading information, or manipulative behavior</li>
              <li>Content promoting illegal activities or services</li>
              <li>Sexually explicit or violent content</li>
              <li>Deceptive or misleading information</li>
              <li>Intellectual property violations or plagiarism</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Marketplace Rules</h2>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Seller Requirements</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Must meet qualification thresholds (Level A, 10+ free prompts, 25+ ratings given)</li>
                <li>• Must maintain minimum 7.0 average rating on free content</li>
                <li>• Account must be in good standing (no violations)</li>
              </ul>
            </div>
            <div className="space-y-3 text-gray-700 dark:text-gray-300">
              <p><strong>Price Cap:</strong> Maximum price of 200 Boop Coins ($10 USD equivalent)</p>
              <p><strong>Content Ownership:</strong> You represent that paid content is original or properly licensed</p>
              <p><strong>Platform Fee:</strong> 18% of sale price goes to platform operations and creator fund</p>
              <p><strong>Refund Policy:</strong> 90-day refund window for defective or misrepresented content</p>
              <p><strong>Violations:</strong> Content violations result in marketplace suspension or permanent ban</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Virtual Currency (Boop Coins)</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Boop Coins are virtual currency with no real-world cash value outside the Platform</li>
              <li>Boop Coins are non-refundable once used for features or purchases</li>
              <li>Coin purchases are final (except where legally required to provide refunds)</li>
              <li>We may adjust coin pricing with 30-day advance notice</li>
              <li>Unused coins remain valid unless your account is terminated for violations</li>
              <li>Coins cannot be transferred between accounts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Payouts</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">For sellers converting Boop Coins to cash:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Minimum payout threshold: 500 Boop Coins</li>
              <li>Conversion rate: 80% of coin purchase value</li>
              <li>Processing time: 5-7 business days</li>
              <li>Tax reporting required for $600+ annual earnings (US residents)</li>
              <li>We may suspend payouts for suspected fraud or policy violations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Premium Features</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Features and pricing are subject to change with notice</li>
              <li>No refunds on feature usage once coins are spent</li>
              <li>We may discontinue features with 30-day advance notice</li>
              <li>We do not guarantee feature availability or uptime</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Prohibited Activities</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">You may not:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Game, manipulate, or artificially inflate ratings or metrics</li>
              <li>Create fake accounts or use automated systems (bots)</li>
              <li>Purchase your own marketplace items to manipulate rankings</li>
              <li>Scrape, harvest, or extract data without permission</li>
              <li>Reverse engineer, decompile, or access platform source code</li>
              <li>Circumvent access controls, rate limits, or security measures</li>
              <li>Impersonate other users, staff, or entities</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Intellectual Property</h2>
            <p className="text-gray-700 dark:text-gray-300">
              The Boop name, logo, design, platform code, and algorithms are proprietary and protected by copyright,
              trademark, and other intellectual property laws. User-generated content remains the property of the
              respective creators.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Account Termination</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              We may suspend or terminate accounts for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Violations of these Terms of Service</li>
              <li>Fraudulent activity or abuse of the platform</li>
              <li>Legal requirements or court orders</li>
              <li>Extended inactivity (with advance notice)</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-3">
              You may delete your account at any time through Settings. Account deletion removes personal information
              but may anonymize your public content to preserve community context.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Disclaimers</h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>We do not guarantee uptime, availability, or uninterrupted access</li>
                <li>We are not responsible for user-generated content</li>
                <li>We do not guarantee the effectiveness or accuracy of prompts</li>
                <li>We are not liable for third-party integrations or AI model outputs</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">13. Limitation of Liability</h2>
            <p className="text-gray-700 dark:text-gray-300">
              To the maximum extent permitted by law, Boop shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or
              indirectly, or any loss of data, use, goodwill, or other intangible losses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">14. Dispute Resolution</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Governing Law:</strong> These Terms shall be governed by the laws of [Jurisdiction], without
              regard to conflict of law provisions.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Arbitration:</strong> Any disputes arising from these Terms or use of the Service shall be
              resolved through binding arbitration.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <strong>Class Action Waiver:</strong> You agree to resolve disputes individually and waive the right
              to participate in class action lawsuits.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">15. Changes to Terms</h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update these Terms from time to time. We will notify users of material changes via email and
              on-site notification. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">16. Contact</h2>
            <p className="text-gray-700 dark:text-gray-300">
              For questions about these Terms, please contact us at{' '}
              <a href="mailto:legal@boop.com" className="text-purple-600 dark:text-purple-400 hover:underline">
                legal@boop.com
              </a>
            </p>
          </section>

          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 mt-8">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              By using Boop, you acknowledge that you have read, understood, and agree to be bound by these Terms of
              Service. If you do not agree to these terms, please discontinue use of the Service immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
